import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Popover, List, ListItem, ListItemText, ListItemAvatar, Divider,
  Button, Avatar, CircularProgress, Snackbar, Typography, Box,
} from "@mui/material";
import { ChatBubbleOutline as ChatIcon } from "@mui/icons-material";
import {
  Search, Bell, ChevronRight, Star, BarChart3, Boxes, Gauge, PenLine,
  ClipboardList, Clock, MessagesSquare, Wrench, FolderOpen, Store, FileText,
  Receipt, AlertTriangle, Image as ImageIcon, Sparkles, Activity, ScanSearch, CreditCard,
  ShieldAlert, ChevronUp, ChevronDown, X as XIcon, ExternalLink,
} from "lucide-react";
import api from "../services/api";
import IDCardPage from "./IDCardPage";

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

const SECTIONS = [
  { id: "analysis",  label: "Data & Analysis",      icon: BarChart3,    tintBg: "#ede9fe", tintColor: "#7c3aed", ringColor: "rgba(196,181,253,0.5)" },
  { id: "projects",  label: "Project Management",   icon: ClipboardList, tintBg: "#f0fdfa", tintColor: "#0d9488", ringColor: "rgba(153,246,228,0.5)" },
  { id: "tools",     label: "Tools & Resources",    icon: Wrench,        tintBg: "#fffbeb", tintColor: "#d97706", ringColor: "rgba(253,230,138,0.5)" },
  { id: "accounts",  label: "Finance",              icon: FileText,      tintBg: "#eef2ff", tintColor: "#4f46e5", ringColor: "rgba(199,210,254,0.5)" },
];

const APPLICATION_BUTTONS = [
  { id: "norms",          title: "Norms",                icon: Boxes,        route: "/home",                      description: "Product, Pack, Concept Testing Norms",   group: "analysis" },
  { id: "bcm_norms",      title: "BCM Norms",            icon: Boxes,        route: "/bcm_norms/file-manager",   description: "BCM Norms",                               group: "analysis" },
  { id: "drivers",        title: "Driver Analysis",      icon: Gauge,        route: "/driver_analysis",           description: "Analyze key drivers of performance",      group: "analysis" },
  { id: "oe_coding",      title: "Open End Coding",      icon: PenLine,      route: "/oe-coding",                 description: "AI-powered open end response coding",     group: "analysis", badge: "AI" },
  { id: "jobs",           title: "Job Tracker",          icon: ClipboardList, route: "/projects_list",           description: "Track and manage project jobs",           group: "projects", showBadgeFor: "jobs" },
  { id: "timesheet",      title: "Timesheet",            icon: Clock,        route: "/timesheet",                 description: "Track time and attendance",               group: "projects" },
  { id: "discussion",     title: "Discussions",          icon: MessagesSquare, route: "/discussions",            description: "Collaborate on projects and segments",    group: "projects", showBadgeFor: "discussions" },
  { id: "awn",            title: "Analysis Warning Note", icon: AlertTriangle, route: "/awn-form",               description: "Create and manage analysis warnings",     group: "tools" },
  { id: "resize",         title: "Image Resizing",       icon: ImageIcon,    route: "/img-resize",                description: "Resize and optimize images",              group: "tools" },
  { id: "checklists",     title: "Resources",            icon: FolderOpen,   route: "/documents_and_checklists",  description: "Manage documents and checklists",         group: "tools" },
  { id: "vendors",        title: "Vendors",              icon: Store,        route: "/vendors",                   description: "Vendor management portal",                group: "tools" },
  { id: "accounts",       title: "PO Tracker",           icon: FileText,     route: "/accounts",                  description: "Track purchase orders",                   group: "accounts" },
  { id: "accounts-report", title: "PO Report",           icon: BarChart3,    route: "/accounts-report",           description: "Invoice analytics and trends",            group: "accounts" },
  { id: "expenses",       title: "Expense Tracker",      icon: Receipt,      route: "/expenses",                  description: "Monthly expense reports and invoices",    group: "accounts" },
  { id: "data_qc",        title: "Data QC",              icon: ScanSearch,   route: "/data-qc",                   description: "Survey logic QC — skip, display, full base checks", group: "analysis" },
  { id: "idcard",         title: "ID Card",              icon: CreditCard,   route: "/idcard",                    description: "Generate and print employee ID cards",            group: "tools" },
  { id: "platform_issues", title: "Platform Issues",    icon: ShieldAlert,  route: "/platform-issues",           description: "Track bugs and logic issues in XEBO, SurveyToGo, Forsta", group: "tools" },
];

const ACCOUNTS_REPORT_AUTHORIZED_USERS = ["faiz@4sight-global.com", "ram@4sight-global.com"];
const BCM_NORMS_AUTHORIZED_USERS = [
  "krati@4sight-global.com", "vaibhav@4sight-global.com", "aarushi@4sight-global.com",
  "tanuj@4sight-global.com", "jazim@4sight-global.com", "ram@4sight-global.com",
  "jyothi@4sight-global.com", "varun@4sight-global.com",
];

// ---------------------------------------------------------------------------
// AppCard
// ---------------------------------------------------------------------------

function AppCard({ app, section, unreadCount, pinned, onTogglePin, onClick }) {
  const Icon = app.icon;
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={(e) => onClick(app.route, e)}
      style={{
        position: "relative",
        display: "flex",
        alignItems: "flex-start",
        gap: "12px",
        borderRadius: "16px",
        border: `1px solid ${hovered ? "#cbd5e1" : "#e2e8f0"}`,
        backgroundColor: "white",
        padding: "16px",
        cursor: "pointer",
        transition: "all 0.18s ease",
        transform: hovered ? "translateY(-2px)" : "none",
        boxShadow: hovered ? "0 4px 20px rgba(0,0,0,0.08)" : "none",
        userSelect: "none",
      }}
    >
      {/* Colored icon tile */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        width: 44, height: 44, borderRadius: "12px", flexShrink: 0,
        backgroundColor: section.tintBg, color: section.tintColor,
        boxShadow: `0 0 0 4px ${section.ringColor}`,
      }}>
        <Icon size={19} strokeWidth={1.8} />
      </div>

      {/* Text */}
      <div style={{ flex: 1, minWidth: 0, paddingRight: "24px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
          <span style={{ fontSize: "14px", fontWeight: 600, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {app.title}
          </span>
          {app.badge && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: "2px", borderRadius: "100px", backgroundColor: "rgba(13,148,136,0.1)", padding: "2px 6px" }}>
              <Sparkles size={9} color="#0d9488" />
              <span style={{ fontSize: "10px", fontWeight: 700, color: "#0d9488", lineHeight: 1 }}>{app.badge}</span>
            </span>
          )}
          {app.showBadgeFor && unreadCount > 0 && (
            <span style={{ backgroundColor: "#ef4444", color: "white", borderRadius: "100px", padding: "0 6px", fontSize: "10px", fontWeight: 700, lineHeight: "16px" }}>
              {unreadCount}
            </span>
          )}
        </div>
        <div style={{ fontSize: "12.5px", color: "#64748b", marginTop: "2px", lineHeight: 1.4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {app.description}
        </div>
      </div>

      {/* Pin button */}
      <button
        onClick={e => { e.stopPropagation(); onTogglePin(app.id); }}
        title={pinned ? "Unpin" : "Pin to quick access"}
        style={{
          position: "absolute", top: 10, right: 10,
          padding: "4px", borderRadius: "6px", border: "none", cursor: "pointer",
          backgroundColor: "transparent", display: "flex", alignItems: "center",
          color: pinned ? "#f59e0b" : "#cbd5e1",
          opacity: hovered || pinned ? 1 : 0,
          transition: "all 0.15s",
        }}
      >
        <Star size={14} fill={pinned ? "currentColor" : "none"} />
      </button>

      {/* Chevron arrow */}
      <div style={{
        position: "absolute", bottom: 14, right: 10,
        color: hovered ? section.tintColor : "#cbd5e1",
        transition: "all 0.15s",
        display: "flex",
      }}>
        <ChevronRight size={16} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// App grid
// ---------------------------------------------------------------------------

function AppGrid({ apps, section, pinnedIds, onTogglePin, unreadCount, onCardClick }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "12px" }}>
      {apps.map(app => (
        <AppCard key={app.id} app={app} section={section} unreadCount={unreadCount}
          pinned={pinnedIds.includes(app.id)} onTogglePin={onTogglePin} onClick={onCardClick} />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function ProfessionalDashboard() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [pinnedIds, setPinnedIds] = useState(["discussion", "jobs"]);

  const [unreadCount, setUnreadCount] = useState(0);
  const [prevUnreadCount, setPrevUnreadCount] = useState(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [notifAnchor, setNotifAnchor] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [notifsLoading, setNotifsLoading] = useState(false);
  const [expenseAnchor, setExpenseAnchor] = useState(null);
  const [expensePickerYear, setExpensePickerYear] = useState(new Date().getFullYear());

  const [dmThreads, setDmThreads] = useState([]);
  const [dmStatuses, setDmStatuses] = useState({});
  const [dashboardData, setDashboardData] = useState({ due_today: [], recently_delivered: [], recently_created_awn: [] });
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [platformIssues, setPlatformIssues] = useState([]);
  const [platformBannerVisible, setPlatformBannerVisible] = useState(
    () => localStorage.getItem('platformBannerHidden') !== 'true'
  );
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [issueSearch, setIssueSearch] = useState('');
  const [issueModalResults, setIssueModalResults] = useState([]);
  const [issueModalLoading, setIssueModalLoading] = useState(false);
  const mountedRef = useRef(true);
  const notifPermAsked = useRef(false);
  const username = localStorage.getItem("username") || "";
  const isAdmin = localStorage.getItem("is_admin") === "true";
  const canViewActivity = isAdmin || localStorage.getItem("can_view_activity") === "true";
  const [userProfileData, setUserProfileData] = useState({ designation: "", department: "" });

  const fetchDashboardSummary = useCallback(async () => {
    try {
      const res = await api.get('api/dashboard/summary/');
      if (mountedRef.current) setDashboardData(res.data || { due_today: [], recently_delivered: [], recently_created_awn: [] });
    } catch { } finally { if (mountedRef.current) setDashboardLoading(false); }
  }, []);

  const openSegmentDetail = useCallback((seg) => {
    navigate('/project_detailed_view', { state: { project: seg } });
  }, [navigate]);

  const fetchPlatformIssues = useCallback(async () => {
    try {
      const res = await api.get('api/platform-issues/issues/home_widget/');
      if (mountedRef.current) setPlatformIssues(res.data || []);
    } catch { }
  }, []);

  const searchIssues = useCallback(async (q) => {
    setIssueModalLoading(true);
    try {
      const params = q ? `?search=${encodeURIComponent(q)}` : '';
      const res = await api.get(`api/platform-issues/issues/${params}`);
      setIssueModalResults(res.data || []);
    } catch { } finally { setIssueModalLoading(false); }
  }, []);

  useEffect(() => {
    if (!showIssueModal) return;
    const t = setTimeout(() => searchIssues(issueSearch), 280);
    return () => clearTimeout(t);
  }, [showIssueModal, issueSearch, searchIssues]);

  const fetchDmThreads = useCallback(() => {
    const tkn = localStorage.getItem("Token") || localStorage.getItem("token");
    if (!tkn) return;
    const API = process.env.REACT_APP_API_URL || '';
    axios.get(`${API}/api/dm/threads/`, { headers: { Authorization: `Token ${tkn}` } })
      .then(res => { if (mountedRef.current) setDmThreads((res.data || []).slice(0, 10)); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const tkn = localStorage.getItem("Token") || localStorage.getItem("token");
    if (!tkn) return;
    api.get("users/profile/", { headers: { Authorization: `Token ${tkn}` } })
      .then(res => setUserProfileData({ designation: res.data.designation || "", department: res.data.department || "" }))
      .catch(() => {});
    const API = process.env.REACT_APP_API_URL || '';
    axios.get(`${API}/users/statuses/`, { headers: { Authorization: `Token ${tkn}` } })
      .then(res => setDmStatuses(res.data || {}))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchDashboardSummary();
    const id = setInterval(fetchDashboardSummary, 60000);
    return () => clearInterval(id);
  }, [fetchDashboardSummary]);

  useEffect(() => {
    fetchPlatformIssues();
    const id = setInterval(fetchPlatformIssues, 120000);
    return () => clearInterval(id);
  }, [fetchPlatformIssues]);

  useEffect(() => {
    fetchDmThreads();
    const id = setInterval(fetchDmThreads, 8000);
    return () => clearInterval(id);
  }, [fetchDmThreads]);

  useEffect(() => {
    if (!notifPermAsked.current && "Notification" in window && Notification.permission === "default") {
      notifPermAsked.current = true;
      Notification.requestPermission();
    }
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    try {
      if (!username) return;
      const res = await api.get(`discussion/total-unread-count/?username=${username}`);
      const newCount = res?.data?.total_unread_count || 0;
      if (!mountedRef.current) return;
      if (newCount > prevUnreadCount) {
        setSnackbarOpen(true);
        if ("Notification" in window && Notification.permission === "granted" && prevUnreadCount > 0) {
          const diff = newCount - prevUnreadCount;
          new Notification("4Sight Operations", {
            body: `You have ${diff} new unread message${diff > 1 ? "s" : ""}`,
            icon: "/notification_icon.png",
            tag: "discussion-unread",
          });
        }
      }
      setPrevUnreadCount(newCount);
      setUnreadCount(newCount);
    } catch { }
  }, [prevUnreadCount, username]);

  useEffect(() => {
    mountedRef.current = true;
    fetchUnreadCount();
    const id = setInterval(fetchUnreadCount, 8000);
    return () => { mountedRef.current = false; clearInterval(id); };
  }, [fetchUnreadCount]);

  const fetchNotifications = async () => {
    setNotifsLoading(true);
    try {
      const res = await api.get("discussion/unread-messages/");
      setNotifications((res.data?.unread_messages || []).slice(0, 50));
    } catch { } finally { setNotifsLoading(false); }
  };

  const formatNotifTime = (timestamp) => {
    const diffMins = Math.floor((Date.now() - new Date(timestamp)) / 60000);
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return new Date(timestamp).toLocaleDateString();
  };

  const [showIdCard, setShowIdCard] = useState(false);

  const handleCardClick = (route, event) => {
    const app = APPLICATION_BUTTONS.find(a => a.route === route);
    if (app) api.post("users/log-activity/", { page: app.title }).catch(() => {});
    if (route === "/expenses") {
      setExpensePickerYear(new Date().getFullYear());
      setExpenseAnchor(event.currentTarget);
      return;
    }
    if (route === "/idcard") {
      setShowIdCard(true);
      return;
    }
    navigate(route);
  };

  const togglePin = (id) =>
    setPinnedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const accessibleApplications = useMemo(() => {
    const email = username.toLowerCase();
    const desig = userProfileData.designation.toLowerCase();
    const dept = userProfileData.department.toLowerCase();
    const isLocalhost = ["localhost", "127.0.0.1"].includes(window.location.hostname);
    return APPLICATION_BUTTONS.filter(app => {
      if (app.localhostOnly && !isLocalhost) return false;
      if (app.id === "bcm_norms") return BCM_NORMS_AUTHORIZED_USERS.includes(email) || desig === "head of data operations";
      if (app.id === "accounts-report") return ACCOUNTS_REPORT_AUTHORIZED_USERS.includes(email);
      if (app.id === "expenses") return isAdmin || desig === "head of data operations" || dept === "accounts" || dept === "finance";
      if (app.id === "awn") return isAdmin || dept === "research";
      if (app.id === "data_qc") return isAdmin || dept === "data processing" || dept === "data operations";
      return true;
    });
  }, [username, userProfileData, isAdmin]);

  const filteredApplications = useMemo(() => {
    const q = query.trim().toLowerCase();
    return accessibleApplications.filter(app =>
      (activeCategory === "all" || app.group === activeCategory) &&
      (!q || app.title.toLowerCase().includes(q) || app.description.toLowerCase().includes(q) || app.id.toLowerCase().includes(q))
    );
  }, [query, activeCategory, accessibleApplications]);

  const groupedApps = useMemo(() => {
    const groups = {};
    for (const s of SECTIONS) {
      const apps = filteredApplications.filter(a => a.group === s.id);
      if (apps.length > 0) groups[s.id] = apps;
    }
    return groups;
  }, [filteredApplications]);

  const pinnedApps = useMemo(() =>
    accessibleApplications.filter(a => pinnedIds.includes(a.id)),
    [accessibleApplications, pinnedIds]
  );

  // ── Mini chat helpers ──────────────────────────────────────────────────────
  const AVATAR_PALETTE = ["#14b8a6","#6366f1","#f59e0b","#ec4899","#8b5cf6","#0ea5e9","#10b981","#ef4444"];
  const dmAvatarColor = (name) => AVATAR_PALETTE[(name || '').charCodeAt(0) % AVATAR_PALETTE.length];
  const dmInitials = (name) => (name || '?').split(' ').slice(0,2).map(p => p[0]).join('').toUpperCase();
  const stripHtml = (html) => (html || '').replace(/<[^>]+>/g, '').trim();
  const dmTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso), now = new Date();
    const diffMins = Math.floor((now - d) / 60000);
    if (diffMins < 1) return 'now';
    if (diffMins < 60) return `${diffMins}m`;
    const h = Math.floor(diffMins / 60);
    if (h < 24) return `${h}h`;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  };

  // Chip button style
  const chipStyle = (active) => ({
    borderRadius: "100px",
    padding: "6px 14px",
    fontSize: "12.5px",
    fontWeight: 500,
    cursor: "pointer",
    border: `1px solid ${active ? "#0f172a" : "#e2e8f0"}`,
    backgroundColor: active ? "#0f172a" : "white",
    color: active ? "white" : "#64748b",
    transition: "all 0.15s",
    fontFamily: "inherit",
    lineHeight: "normal",
  });

  // Header button style
  const headerBtnStyle = (hasNotif) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 14px",
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    backgroundColor: "white",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: 500,
    color: "#475569",
    fontFamily: "inherit",
    lineHeight: "normal",
    position: "relative",
  });


  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f8fafc" }}>
      <div style={{ maxWidth: "1440px", margin: "0 auto", padding: "32px", display: "flex", gap: "24px", alignItems: "flex-start" }}>
      {/* ── Main content ── */}
      <div style={{ flex: 1, minWidth: 0 }}>

        {/* Page header row */}
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", justifyContent: "space-between", gap: "16px", marginBottom: "24px" }}>
          <div>
            <div style={{ fontSize: "28px", fontWeight: 700, letterSpacing: "-0.3px", color: "#0f172a", lineHeight: 1.2 }}>Applications</div>
            <div style={{ fontSize: "13.5px", color: "#64748b", marginTop: "4px" }}>Access your tools from a single workspace</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {canViewActivity && (
              <button onClick={() => navigate("/admin-activity")} style={headerBtnStyle()}>
                <Activity size={14} /> User activity
              </button>
            )}
            <button onClick={e => { setNotifAnchor(e.currentTarget); fetchNotifications(); }} style={headerBtnStyle(true)}>
              <Bell size={14} /> Notifications
              {unreadCount > 0 && (
                <span style={{
                  position: "absolute", top: -4, right: -4,
                  width: 16, height: 16, borderRadius: "50%",
                  backgroundColor: "#ef4444", display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "9px", fontWeight: 700, color: "white",
                }}>
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Notifications popover */}
        <Popover
          open={Boolean(notifAnchor)} anchorEl={notifAnchor}
          onClose={() => setNotifAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{ sx: { width: 400, maxHeight: 500, borderRadius: "12px", boxShadow: "0 8px 32px rgba(0,0,0,0.12)", mt: "4px" } }}
        >
          <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography sx={{ fontWeight: 700, fontSize: "14px" }}>
              Notifications {unreadCount > 0 && `(${unreadCount})`}
            </Typography>
            <Button size="small" onClick={() => { setNotifAnchor(null); navigate("/discussions"); }}>View All</Button>
          </Box>
          <List sx={{ p: 0, overflowY: "auto", maxHeight: 420 }}>
            {notifsLoading ? (
              <ListItem sx={{ justifyContent: "center" }}><CircularProgress size={20} /></ListItem>
            ) : notifications.length === 0 ? (
              <ListItem><ListItemText primary="No unread notifications" sx={{ textAlign: "center", color: "text.secondary" }} /></ListItem>
            ) : notifications.map((n, i) => (
              <React.Fragment key={i}>
                <ListItem
                  sx={{ cursor: "pointer", "&:hover": { bgcolor: "#f8fafc" }, alignItems: "flex-start", py: 1.5 }}
                  onClick={() => { setNotifAnchor(null); navigate(`/discussion?segment_id=${n.segment_id}`); }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: "rgba(13,148,136,0.1)", color: "#0d9488", width: 36, height: 36 }}>
                      <ChatIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.8rem" }}>{n.poster_name}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem", whiteSpace: "nowrap", ml: 1 }}>{formatNotifTime(n.timestamp)}</Typography>
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography variant="caption" sx={{ color: "#0d9488", fontWeight: 600, display: "block", mb: "2px" }}>{n.segment_name}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.78rem", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{n.comment}</Typography>
                      </>
                    }
                  />
                </ListItem>
                {i < notifications.length - 1 && <Divider component="li" />}
              </React.Fragment>
            ))}
          </List>
        </Popover>

        {/* ── Platform Issues Banner ── */}
        {platformIssues.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            {/* Banner header */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: platformBannerVisible ? 10 : 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, flex: 1 }}>
                <ShieldAlert size={14} color="#6366f1" />
                <span style={{ fontSize: 12, fontWeight: 700, color: "#6366f1", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Platform Issues
                </span>
                <span style={{ background: "#eef2ff", color: "#6366f1", borderRadius: 10, padding: "1px 8px", fontSize: 11, fontWeight: 700 }}>
                  {platformIssues.length} active
                </span>
              </div>
              <button
                onClick={() => { setShowIssueModal(true); }}
                style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 12px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                <Search size={11} /> Search all issues
              </button>
              <button
                onClick={() => {
                  const next = !platformBannerVisible;
                  setPlatformBannerVisible(next);
                  try { localStorage.setItem('platformBannerHidden', next ? 'false' : 'true'); } catch {}
                }}
                style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 10px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", color: "#94a3b8", fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                {platformBannerVisible ? <><ChevronUp size={12} /> Hide</> : <><ChevronDown size={12} /> Show</>}
              </button>
            </div>

            {/* Scrollable cards */}
            {platformBannerVisible && (
              <div style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 6 }}>
                {platformIssues.map(issue => {
                  const PLAT = { XEBO: { color: "#6366f1", bg: "#eef2ff" }, SURVEYTOGO: { color: "#0d9488", bg: "#f0fdfa" }, FORSTA: { color: "#d97706", bg: "#fffbeb" } };
                  const p = PLAT[issue.platform] || { color: "#64748b", bg: "#f1f5f9" };
                  const platLabel = issue.platform === "SURVEYTOGO" ? "SurveyToGo" : issue.platform;
                  const isOpen = issue.status === "OPEN";
                  return (
                    <div key={issue.id}
                      onClick={() => { setShowIssueModal(true); setIssueSearch(issue.title); }}
                      style={{ flexShrink: 0, width: 280, background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "13px 15px", cursor: "pointer", transition: "box-shadow 0.15s" }}
                      onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 16px rgba(99,102,241,0.12)"}
                      onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 7 }}>
                        <span style={{ fontSize: 10.5, fontWeight: 700, color: p.color, background: p.bg, borderRadius: 5, padding: "2px 7px" }}>{platLabel}</span>
                        <span style={{ fontSize: 10.5, fontWeight: 600, color: isOpen ? "#ef4444" : "#f59e0b", background: isOpen ? "#fef2f2" : "#fffbeb", borderRadius: 5, padding: "2px 7px" }}>
                          {isOpen ? "Open" : "In Progress"}
                        </span>
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", lineHeight: 1.35, marginBottom: 5, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical" }}>
                        {issue.title}
                      </div>
                      {issue.description && (
                        <div style={{ fontSize: 11.5, color: "#64748b", lineHeight: 1.45, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                          {issue.description}
                        </div>
                      )}
                    </div>
                  );
                })}
                <div
                  onClick={() => navigate("/platform-issues")}
                  style={{ flexShrink: 0, width: 120, background: "#f8fafc", borderRadius: 12, border: "2px dashed #e2e8f0", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, cursor: "pointer", padding: "13px 15px" }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = "#6366f1"}
                  onMouseLeave={e => e.currentTarget.style.borderColor = "#e2e8f0"}>
                  <ExternalLink size={14} color="#6366f1" />
                  <span style={{ fontSize: 11.5, fontWeight: 600, color: "#6366f1", textAlign: "center" }}>View all</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Issue Search Modal ── */}
        {showIssueModal && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: 80 }}>
            <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 620, maxHeight: "70vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.18)", overflow: "hidden" }}>
              {/* Modal header */}
              <div style={{ padding: "16px 20px", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: 10 }}>
                <ShieldAlert size={16} color="#6366f1" />
                <span style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", flex: 1 }}>Platform Issues</span>
                <button onClick={() => { setShowIssueModal(false); setIssueSearch(''); }} style={{ border: "none", background: "transparent", cursor: "pointer", color: "#94a3b8", display: "flex", padding: 4, borderRadius: 6 }}><XIcon size={16} /></button>
              </div>
              {/* Search input */}
              <div style={{ padding: "12px 20px", borderBottom: "1px solid #f1f5f9", position: "relative" }}>
                <Search size={14} style={{ position: "absolute", left: 34, top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
                <input
                  autoFocus
                  value={issueSearch}
                  onChange={e => setIssueSearch(e.target.value)}
                  placeholder="Search by title, description, category…"
                  style={{ width: "100%", boxSizing: "border-box", paddingLeft: 34, paddingRight: 12, paddingTop: 9, paddingBottom: 9, borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13.5, color: "#0f172a", outline: "none", fontFamily: "inherit" }}
                />
              </div>
              {/* Results */}
              <div style={{ flex: 1, overflowY: "auto" }}>
                {issueModalLoading ? (
                  <div style={{ padding: "24px", textAlign: "center", color: "#94a3b8", fontSize: 13 }}>Searching…</div>
                ) : issueModalResults.length === 0 ? (
                  <div style={{ padding: "24px", textAlign: "center", color: "#94a3b8", fontSize: 13 }}>No issues found</div>
                ) : issueModalResults.map(issue => {
                  const PLAT = { XEBO: { color: "#6366f1", bg: "#eef2ff" }, SURVEYTOGO: { color: "#0d9488", bg: "#f0fdfa" }, FORSTA: { color: "#d97706", bg: "#fffbeb" } };
                  const p = PLAT[issue.platform] || { color: "#64748b", bg: "#f1f5f9" };
                  const platLabel = issue.platform === "SURVEYTOGO" ? "SurveyToGo" : issue.platform;
                  const STATUS_COLORS = { OPEN: "#ef4444", IN_PROGRESS: "#f59e0b", RESOLVED: "#16a34a", CLOSED: "#64748b" };
                  const STATUS_BG = { OPEN: "#fef2f2", IN_PROGRESS: "#fffbeb", RESOLVED: "#f0fdf4", CLOSED: "#f8fafc" };
                  const statusLabel = { OPEN: "Open", IN_PROGRESS: "In Progress", RESOLVED: "Resolved", CLOSED: "Closed" }[issue.status] || issue.status;
                  return (
                    <div key={issue.id}
                      onClick={() => { navigate("/platform-issues"); setShowIssueModal(false); }}
                      style={{ padding: "13px 20px", borderBottom: "1px solid #f1f5f9", cursor: "pointer" }}
                      onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 10.5, fontWeight: 700, color: p.color, background: p.bg, borderRadius: 5, padding: "2px 7px" }}>{platLabel}</span>
                        <span style={{ fontSize: 10.5, fontWeight: 600, color: STATUS_COLORS[issue.status], background: STATUS_BG[issue.status], borderRadius: 5, padding: "2px 7px" }}>{statusLabel}</span>
                        {issue.category && <span style={{ fontSize: 10.5, color: "#64748b", background: "#f1f5f9", borderRadius: 5, padding: "2px 7px" }}>{issue.category}</span>}
                      </div>
                      <div style={{ fontSize: 13.5, fontWeight: 600, color: "#0f172a", marginBottom: 3 }}>{issue.title}</div>
                      <div style={{ fontSize: 12, color: "#64748b", overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                        {issue.description || "No description"}
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Footer */}
              <div style={{ padding: "10px 20px", borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "flex-end" }}>
                <button onClick={() => { navigate("/platform-issues"); setShowIssueModal(false); }}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 16px", borderRadius: 8, border: "none", background: "#0f172a", color: "#fff", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                  <ExternalLink size={12} /> Open full page
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Search */}
        <div style={{ position: "relative", marginBottom: "16px" }}>
          <div style={{ position: "absolute", left: "16px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8", pointerEvents: "none", display: "flex" }}>
            <Search size={16} />
          </div>
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search applications…"
            style={{
              width: "100%", boxSizing: "border-box",
              borderRadius: "16px", border: "1px solid #e2e8f0",
              backgroundColor: "white", padding: "12px 16px 12px 44px",
              fontSize: "14px", color: "#334155", outline: "none",
              boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
              fontFamily: "inherit",
            }}
          />
        </div>

        {/* Category filter chips */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "32px" }}>
          <button onClick={() => setActiveCategory("all")} style={chipStyle(activeCategory === "all")}>All apps</button>
          {SECTIONS.map(s => (
            <button key={s.id} onClick={() => setActiveCategory(s.id)} style={chipStyle(activeCategory === s.id)}>{s.label}</button>
          ))}
        </div>

        {/* Quick access (pinned) */}
        {activeCategory === "all" && !query && pinnedApps.length > 0 && (
          <div style={{ marginBottom: "36px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "12px" }}>
              <Star size={13} fill="#f59e0b" color="#f59e0b" />
              <span style={{ fontSize: "12px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#94a3b8" }}>Quick access</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "12px" }}>
              {pinnedApps.map(app => {
                const section = SECTIONS.find(s => s.id === app.group) || SECTIONS[0];
                return (
                  <AppCard key={app.id} app={app} section={section} unreadCount={unreadCount}
                    pinned onTogglePin={togglePin} onClick={handleCardClick} />
                );
              })}
            </div>
          </div>
        )}

        {/* Results: search/filter mode OR grouped sections */}
        {query || activeCategory !== "all" ? (
          filteredApplications.length > 0 ? (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "12px" }}>
              {filteredApplications.map(app => {
                const section = SECTIONS.find(s => s.id === app.group) || SECTIONS[0];
                return (
                  <AppCard key={app.id} app={app} section={section} unreadCount={unreadCount}
                    pinned={pinnedIds.includes(app.id)} onTogglePin={togglePin} onClick={handleCardClick} />
                );
              })}
            </div>
          ) : (
            <div style={{ borderRadius: "16px", border: "2px dashed #e2e8f0", backgroundColor: "white", padding: "64px 0", textAlign: "center" }}>
              <div style={{ fontSize: "14px", fontWeight: 600, color: "#475569" }}>No applications match "{query}"</div>
              <div style={{ fontSize: "12.5px", color: "#94a3b8", marginTop: "4px" }}>Try a different search term or clear the filter.</div>
            </div>
          )
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "36px" }}>
            {SECTIONS.map(section => {
              const apps = groupedApps[section.id];
              if (!apps) return null;
              const SIcon = section.icon;
              return (
                <div key={section.id}>
                  {/* Section header */}
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 32, height: 32, borderRadius: "8px", backgroundColor: section.tintBg, color: section.tintColor }}>
                      <SIcon size={15} />
                    </div>
                    <div>
                      <div style={{ fontSize: "15px", fontWeight: 700, color: "#0f172a", lineHeight: 1.2 }}>{section.label}</div>
                      <div style={{ fontSize: "11.5px", color: "#94a3b8" }}>{apps.length} app{apps.length !== 1 ? "s" : ""}</div>
                    </div>
                  </div>
                  {/* App cards */}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "12px" }}>
                    {apps.map(app => (
                      <AppCard key={app.id} app={app} section={section} unreadCount={unreadCount}
                        pinned={pinnedIds.includes(app.id)} onTogglePin={togglePin} onClick={handleCardClick} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>{/* /main content */}

      {/* ── Dashboard panel (3rd column, where apps used to be) — hidden for accounts/finance ── */}
      {!["accounts", "finance"].includes(userProfileData.department.toLowerCase()) && (
      <div style={{ width: 260, flexShrink: 0, position: "sticky", top: 24, display: "flex", flexDirection: "column", gap: 12 }}>

        {/* Due Today */}
        <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>
          <div style={{ background: "#ef4444", padding: "12px 16px", display: "flex", alignItems: "center", gap: 8 }}>
            <Clock size={14} color="#fff" />
            <span style={{ fontSize: 12.5, fontWeight: 700, color: "#fff", flex: 1 }}>Next Due</span>
            <span style={{ background: "rgba(255,255,255,0.25)", borderRadius: 10, padding: "1px 7px", fontSize: 11, fontWeight: 700, color: "#fff" }}>{dashboardData.due_today.length}</span>
          </div>
          <div style={{ maxHeight: 200, overflowY: "auto" }}>
            {dashboardLoading ? (
              <div style={{ padding: "18px 14px", textAlign: "center", color: "#94a3b8", fontSize: 12 }}>Loading…</div>
            ) : dashboardData.due_today.length === 0 ? (
              <div style={{ padding: "18px 14px", textAlign: "center", color: "#94a3b8", fontSize: 12 }}>No upcoming deliverables</div>
            ) : dashboardData.due_today.map(seg => {
              const todayStr = new Date().toISOString().slice(0, 10);
              const dueDates = [
                { label: 'Output', date: seg.expected_date_of_output },
                { label: 'Test Link', date: seg.expected_date_of_link },
                { label: 'Final Output', date: seg.expected_date_of_final_output },
                { label: 'Topline', date: seg.latest_topline_date },
              ].filter(d => d.date && d.date >= todayStr).sort((a, b) => a.date.localeCompare(b.date));
              return (
              <div key={seg.id}
                onClick={() => openSegmentDetail(seg)}
                style={{ padding: "8px 14px", borderBottom: "1px solid #fef2f2", cursor: "pointer", transition: "background 0.12s" }}
                onMouseEnter={e => e.currentTarget.style.background = "#fef2f2"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {seg.segment_name || seg.project_name}
                </div>
                <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                  {dueDates.map(d => (
                    <div key={d.label} style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ color: "#94a3b8" }}>{d.label}</span>
                      <span style={{ fontWeight: 600, color: d.date === todayStr ? "#ef4444" : "#f59e0b" }}>
                        {new Date(d.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              );
            })}
          </div>
        </div>

        {/* Recently Delivered */}
        <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>
          <div style={{ background: "#16a34a", padding: "12px 16px", display: "flex", alignItems: "center", gap: 8 }}>
            <Activity size={14} color="#fff" />
            <span style={{ fontSize: 12.5, fontWeight: 700, color: "#fff", flex: 1 }}>Recently Delivered</span>
            <span style={{ background: "rgba(255,255,255,0.25)", borderRadius: 10, padding: "1px 7px", fontSize: 11, fontWeight: 700, color: "#fff" }}>{dashboardData.recently_delivered.length}</span>
          </div>
          <div style={{ maxHeight: 200, overflowY: "auto" }}>
            {dashboardLoading ? (
              <div style={{ padding: "18px 14px", textAlign: "center", color: "#94a3b8", fontSize: 12 }}>Loading…</div>
            ) : dashboardData.recently_delivered.length === 0 ? (
              <div style={{ padding: "18px 14px", textAlign: "center", color: "#94a3b8", fontSize: 12 }}>No recent deliveries</div>
            ) : dashboardData.recently_delivered.map(seg => (
              <div key={seg.id}
                onClick={() => openSegmentDetail(seg)}
                style={{ padding: "8px 14px", borderBottom: "1px solid #f0fdf4", cursor: "pointer", transition: "background 0.12s" }}
                onMouseEnter={e => e.currentTarget.style.background = "#f0fdf4"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {seg.segment_name || seg.project_name}
                </div>
                <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 1, display: "flex", justifyContent: "space-between" }}>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{seg.client || seg.project_name}</span>
                  {seg.actual_date_of_final_output && (
                    <span style={{ flexShrink: 0, marginLeft: 6, color: "#16a34a", fontWeight: 600 }}>
                      {new Date(seg.actual_date_of_final_output).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recently Created AWN */}
        <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>
          <div style={{ background: "#f59e0b", padding: "12px 16px", display: "flex", alignItems: "center", gap: 8 }}>
            <AlertTriangle size={14} color="#fff" />
            <span style={{ fontSize: 12.5, fontWeight: 700, color: "#fff", flex: 1 }}>Recent AWN</span>
            <button onClick={() => navigate("/awn-form")}
              style={{ background: "rgba(255,255,255,0.2)", border: "none", borderRadius: 6, color: "#fff", fontSize: 10, fontWeight: 600, cursor: "pointer", padding: "2px 7px", fontFamily: "inherit" }}>
              View all
            </button>
          </div>
          <div style={{ maxHeight: 200, overflowY: "auto" }}>
            {dashboardLoading ? (
              <div style={{ padding: "18px 14px", textAlign: "center", color: "#94a3b8", fontSize: 12 }}>Loading…</div>
            ) : dashboardData.recently_created_awn.length === 0 ? (
              <div style={{ padding: "18px 14px", textAlign: "center", color: "#94a3b8", fontSize: 12 }}>No AWN entries</div>
            ) : dashboardData.recently_created_awn.map(awn => (
              <div key={awn.id} style={{ padding: "8px 14px", borderBottom: "1px solid #fffbeb" }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {awn.projectName}
                </div>
                <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 1, display: "flex", justifyContent: "space-between" }}>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{awn.jobNumber} · {awn.projectType}</span>
                  {awn.submissionDate && (
                    <span style={{ flexShrink: 0, marginLeft: 6, color: "#d97706", fontWeight: 600 }}>
                      {new Date(awn.submissionDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>


      </div>
      )}

      {/* ── Chat panel (far right) ── */}
      <div style={{ width: 280, flexShrink: 0, position: "sticky", top: 24 }}>

        {/* Mini chat panel */}
        <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>
          <div style={{ background: "#14b8a6", padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 16 }}>💬</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>Recent Chats</span>
            </div>
            <button onClick={() => navigate("/messages")}
              style={{ background: "rgba(255,255,255,0.2)", border: "none", borderRadius: 6, color: "#fff", fontSize: 11, fontWeight: 600, cursor: "pointer", padding: "3px 8px", fontFamily: "inherit" }}>
              View all
            </button>
          </div>
          {(() => {
            const sortByRecent = arr => [...arr].sort((a, b) => {
              const ta = a.last_message?.created_at ? new Date(a.last_message.created_at).getTime() : 0;
              const tb = b.last_message?.created_at ? new Date(b.last_message.created_at).getTime() : 0;
              return tb - ta;
            });
            const people = sortByRecent(dmThreads.filter(t => !t.is_group)).slice(0, 5);
            const groups = sortByRecent(dmThreads.filter(t => t.is_group)).slice(0, 5);

            const renderRow = (t) => {
              const isGroup = t.is_group;
              const name = isGroup ? (t.group_name || "Group") : (t.participants?.[0]?.name || "Unknown");
              const email = isGroup ? "" : (t.participants?.[0]?.email || "");
              const status = isGroup ? null : (dmStatuses[email] || "offline");
              const lastMsg = t.last_message;
              const preview = lastMsg ? `${lastMsg.sender_name}: ${stripHtml(lastMsg.content)}` : "No messages yet";
              const unread = t.unread_count || 0;
              const color = dmAvatarColor(name);
              const photoUrl = !isGroup ? (t.participants?.[0]?.photo_url || null) : null;
              return (
                <div key={t.id} onClick={() => navigate("/messages", { state: { threadId: t.id } })}
                  style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 14px", cursor: "pointer", borderBottom: "1px solid #f8fafc", transition: "background 0.12s" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#f0fdfa"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <div style={{ position: "relative", flexShrink: 0 }}>
                    {photoUrl ? (
                      <img src={photoUrl} alt={name}
                        style={{ width: 34, height: 34, borderRadius: "50%", objectFit: "cover", display: "block" }} />
                    ) : (
                    <div style={{ width: 34, height: 34, borderRadius: isGroup ? 9 : "50%", background: color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: "#fff" }}>
                      {isGroup ? name.slice(0, 1).toUpperCase() : dmInitials(name)}
                    </div>
                    )}
                    {!isGroup && (
                      status === "offline"
                        ? <span style={{ position: "absolute", bottom: 0, right: 0, width: 11, height: 11, borderRadius: "50%", border: "2px solid #fff", background: "#e2e8f0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 7, fontWeight: 900, color: "#64748b", lineHeight: 1 }}>✕</span>
                        : <span style={{ position: "absolute", bottom: 0, right: 0, width: 9, height: 9, borderRadius: "50%", border: "2px solid #fff", background: status === "online" ? "#16a34a" : status === "busy" ? "#f59e0b" : status === "away" ? "#f97316" : "#94a3b8" }} />
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 12.5, fontWeight: unread ? 700 : 600, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 150 }}>{name}</span>
                      <span style={{ fontSize: 10, color: unread ? "#14b8a6" : "#94a3b8", flexShrink: 0, fontWeight: unread ? 700 : 400 }}>{lastMsg ? dmTime(lastMsg.created_at) : ""}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 1 }}>
                      <span style={{ fontSize: 11, color: unread ? "#334155" : "#94a3b8", fontWeight: unread ? 600 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{preview}</span>
                      {unread > 0 && <span style={{ background: "#14b8a6", color: "#fff", borderRadius: 10, padding: "1px 6px", fontSize: 10, fontWeight: 700, flexShrink: 0, marginLeft: 4 }}>{unread > 99 ? "99+" : unread}</span>}
                    </div>
                  </div>
                </div>
              );
            };

            const SectionHeader = ({ label }) => (
              <div style={{ padding: "7px 14px 4px", fontSize: 9.5, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", background: "#f8fafc", borderBottom: "1px solid #f1f5f9" }}>{label}</div>
            );

            if (people.length === 0 && groups.length === 0) {
              return <div style={{ padding: "32px 16px", textAlign: "center", color: "#94a3b8", fontSize: 13 }}>No conversations yet</div>;
            }

            return (
              <div style={{ maxHeight: 340, overflowY: "auto" }}>
                {people.length > 0 && <><SectionHeader label="People" />{people.map(renderRow)}</>}
                {groups.length > 0 && <><SectionHeader label="Groups" />{groups.map(renderRow)}</>}
              </div>
            );
          })()}
          <div style={{ padding: "10px 14px", borderTop: "1px solid #e2e8f0", background: "#f8fafc", display: "flex", gap: 8 }}>
            <button onClick={() => navigate("/messages")}
              style={{ flex: 1, padding: "8px", border: "1px solid #14b8a6", borderRadius: 8, background: "transparent", color: "#0d9488", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
              Open Chat
            </button>
            <button onClick={() => navigate("/discussions")}
              style={{ flex: 1, padding: "8px", border: "1px solid #e2e8f0", borderRadius: 8, background: "transparent", color: "#64748b", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
              Discussions
            </button>
          </div>
        </div>

      </div>

      </div>{/* /flex wrapper */}

      {/* Expense month picker */}
      <Popover
        open={Boolean(expenseAnchor)}
        anchorEl={expenseAnchor}
        onClose={() => setExpenseAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { borderRadius: "16px", boxShadow: "0 8px 32px rgba(0,0,0,0.12)", p: 2, minWidth: 280 } }}
      >
        {/* Year navigation */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <button onClick={() => setExpensePickerYear(y => y - 1)}
            style={{ background: "none", border: "1px solid #e2e8f0", borderRadius: 8, width: 32, height: 32, cursor: "pointer", fontSize: 16, color: "#64748b" }}>‹</button>
          <Typography fontWeight={700} fontSize={15} color="#0f172a">{expensePickerYear}</Typography>
          <button onClick={() => setExpensePickerYear(y => Math.min(y + 1, new Date().getFullYear()))}
            style={{ background: "none", border: "1px solid #e2e8f0", borderRadius: 8, width: 32, height: 32, cursor: "pointer", fontSize: 16, color: "#64748b" }}>›</button>
        </Box>
        {/* Month grid */}
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1 }}>
          {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map((m, i) => {
            const now = new Date();
            const isFuture = expensePickerYear > now.getFullYear() || (expensePickerYear === now.getFullYear() && i > now.getMonth());
            const isCurrent = expensePickerYear === now.getFullYear() && i === now.getMonth();
            return (
              <button key={i} disabled={isFuture}
                onClick={() => { navigate(`/expenses/${expensePickerYear}/${i + 1}`); setExpenseAnchor(null); }}
                style={{
                  padding: "10px 4px", borderRadius: 10, border: isCurrent ? "2px solid #0d9488" : "1px solid #e2e8f0",
                  backgroundColor: isCurrent ? "#f0fdfa" : isFuture ? "#f8fafc" : "#fff",
                  color: isFuture ? "#cbd5e1" : isCurrent ? "#0d9488" : "#334155",
                  fontWeight: isCurrent ? 700 : 500, fontSize: 13, cursor: isFuture ? "not-allowed" : "pointer",
                  transition: "all 0.15s",
                }}>
                {m}
              </button>
            );
          })}
        </Box>
      </Popover>

      {/* New message snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={(_, reason) => { if (reason !== "clickaway") setSnackbarOpen(false); }}
        message="You have new unread messages"
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ "& .MuiSnackbarContent-root": { borderRadius: "12px", bgcolor: "#0d9488", fontWeight: 600, fontSize: "0.875rem" } }}
      />

      {/* ID Card full-screen overlay */}
      {showIdCard && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1200,
          background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(2px)',
          display: 'flex', alignItems: 'stretch',
        }}>
          <div style={{
            marginLeft: 'auto', width: '100%', maxWidth: 860,
            background: '#f1f5f9', overflowY: 'auto',
            position: 'relative',
          }}>
            <button onClick={() => setShowIdCard(false)}
              style={{
                position: 'sticky', top: 16, left: '100%', transform: 'translateX(-100%)',
                marginRight: 20, marginTop: 16, display: 'block',
                background: '#0f172a', color: '#fff', border: 'none',
                borderRadius: 8, padding: '6px 14px', fontSize: 13, fontWeight: 600,
                cursor: 'pointer', zIndex: 10,
              }}>
              ✕ Close
            </button>
            <IDCardPage />
          </div>
        </div>
      )}
    </div>
  );
}
